import type { ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/shared'
import { cn } from '@/lib/utils'

export const CONFIG_SECTIONS = [
  { label: 'Cards', href: '/game-config/cards' },
  { label: 'Arenas', href: '/game-config/arenas' },
  { label: 'Economy', href: '/game-config/economy' },
  { label: 'Matchmaking', href: '/game-config/matchmaking' },
] as const

interface GameConfigShellProps {
  section: string
  description: string
  dirtyCount?: number
  onSave?: () => void
  onRevert?: () => void
  saving?: boolean
  actions?: ReactNode
  children: ReactNode
}

/**
 * The frame every Game Config section shares: title, the section switcher, and
 * the save/revert pair for whatever is on screen.
 *
 * The switcher is navigation, not local state — each section is its own route,
 * so a link can point at one and only that section's rows cross the wire. The
 * rule under the tabs is only as wide as the tabs, which is what lets the
 * actions sit on the same row.
 */
export function GameConfigShell({
  section,
  description,
  dirtyCount = 0,
  onSave,
  onRevert,
  saving,
  actions,
  children,
}: GameConfigShellProps) {
  const current = CONFIG_SECTIONS.find((s) => s.label === section)

  return (
    <AppLayout>
      <Head title={`Game Config · ${section}`} />

      <PageHeader title="Game Config" description={description} actions={actions} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="Config section"
          className="border-border inline-flex w-fit items-center gap-4 border-b"
        >
          {CONFIG_SECTIONS.map((s) => {
            const active = s.href === current?.href
            return (
              <Link
                key={s.href}
                href={s.href}
                role="tab"
                aria-selected={active}
                /* Only the grid changes between sections, so the shell stays
                   mounted and the switch does not flash the whole page. */
                preserveScroll
                className={cn(
                  '-mb-px border-b-2 px-0.5 py-2 text-sm font-medium outline-none',
                  'transition-colors duration-100 ease-out',
                  'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
                  active ? 'border-fg text-fg' : 'text-fg-muted hover:text-fg border-transparent',
                )}
              >
                {s.label}
              </Link>
            )
          })}
        </div>

        {(onSave || onRevert) && (
          <div className="flex items-center gap-2">
            {dirtyCount > 0 && (
              <span className="text-fg-muted text-sm tabular-nums">
                {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
              </span>
            )}
            {onRevert && (
              <Button variant="outline" disabled={dirtyCount === 0 || saving} onClick={onRevert}>
                Revert
              </Button>
            )}
            {onSave && (
              <Button disabled={dirtyCount === 0 || saving} onClick={onSave}>
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
          </div>
        )}
      </div>

      {children}
    </AppLayout>
  )
}
